/**
 * DOM HUD (LOCKED-DESIGN 12.1 / 18.5): a token-styled layered arcade HUD.
 *
 * Layout contract:
 *  - score + timer central with real typographic weight (score counts on change)
 *  - grade meter + chain ladder + dispatch panel down the left column
 *  - shift status + shift-change telegraph + baffle gate down the right column
 *  - overflow edge-pressure meter bottom-right, reticle dock bottom-center,
 *    control-hint strip bottom-left
 *
 * All appearance lives in HudStyles.ts (theme-sourced tokens); this file owns
 * structure + per-frame data binding only. It reads sim getters/ledger through
 * a plain HudModel and never writes back — the fixture hashes cannot move here.
 *
 * Overflow color thresholds consume OVERFLOW_WARNING/OVERFLOW_SEVERE/
 * FACILITY_CAPACITY from simulation/types.ts — the single threshold owner
 * (LOCKED-DESIGN Section 7).
 */
import { FACILITY_CAPACITY, OVERFLOW_SEVERE, OVERFLOW_WARNING } from "../simulation/types.js";
import { edgeBandPlacement, type ChuteIndicatorSpec, type ReservedBandPoint } from "./ChuteIndicators.js";
import { HUD_SAFE_MARGIN_PX } from "./HudReadability.js";
import { cssHex, FEEDBACK_COLORS, GRADE_BAND_HEX, PALETTE } from "./theme.js";
import { CHAIN_TIER_HEX, hudStylesheet, HUD_STYLE_ID, OVERFLOW_BUCKET_HEX, RECEIVER_TIER_HEX } from "./HudStyles.js";
import { RUN_SECONDS } from "../gameplay/RunState.js";

/** One recipe line: pieces required vs live-accumulated across qualifying loads. */
export interface HudRecipeEntry {
  kind: string;
  required: number;
  have: number;
}
export interface HudActiveTicket {
  entries: HudRecipeEntry[];
  receiver: string | null;
  secondsLeft: number;
  windowSeconds: number;
}
export interface HudTicketPreview {
  entries: HudRecipeEntry[];
  receiver: string | null;
  startsIn: number;
}
export interface HudShift {
  phaseIndex: number;
  label: string;
  nextLabel: string;
  secondsToNext: number;
  isShiftEnd: boolean;
  warning: boolean;
  placementSeconds: number;
  changes: string[];
}
export interface HudReticle {
  bulk: number;
  capacity: number;
  composition: Array<{ kind: string; count: number }>;
  redFuse: number | null;
  route: { anchor: string; receiver: string };
  /** Section 8.3 freshness of the committed route (sim-derived, 18.10). */
  freshness: { state: "fresh" | "repeat" | "stale"; delta: number };
  ticketContribution: number | null;
  projectedScore: number;
  /**
   * Best alternative route for the SAME load, present only when switching
   * visibly pays (18.10 fourth dogfood): a strictly better score, or the same
   * score with a strictly better Section 8.3 Grade consequence — so the cell
   * always carries the freshness state that justifies it.
   */
  projectedAlt: {
    anchor: string;
    receiver: string;
    score: number;
    freshness: { state: "fresh" | "repeat" | "stale"; delta: number };
  } | null;
}
export interface HudBaffleGate {
  blocked: boolean;
  secondsToOpen: number;
  secondsToClose: number;
}
/**
 * Overflow location breakdown (LOCKED-DESIGN Section 7 / 18.10): every
 * unprocessed piece counts regardless of location, so the meter names WHERE
 * the bulk is — loose on the floor, held in the beam, in portal transit,
 * rejected in the chamber, or returning on the belt.
 */
export interface HudOverflowBuckets {
  floor: number;
  beam: number;
  transit: number;
  chamber: number;
  belt: number;
}

export interface HudModel {
  score: number;
  timeLeft: number;
  overflow: number;
  overflowBuckets: HudOverflowBuckets;
  grade: number;
  chainTier: number;
  chainMultiplier: number;
  chainFrac: number;
  shift: HudShift;
  activeTicket: HudActiveTicket | null;
  nextTicket: HudTicketPreview | null;
  reticle: HudReticle | null;
  criticalSeconds: number | null;
  baffleGate: HudBaffleGate | null;
  motionReduced: boolean;
}

const KIND_SHORT: Record<string, string> = {
  crate: "CRT", can: "CAN", green: "GRN", red: "RED", husk: "HSK",
};
/** receiver names in the facility's sign voice ("TECH · WALL · ×1.25") */
const RECEIVER_SHORT: Record<string, string> = {
  safe: "SAFE", technical: "TECH", hazard: "HAZARD",
};
const ARROW_GLYPHS = ["→", "↘", "↓", "↙", "←", "↖", "↑", "↗"];
/**
 * Chute intake pips inside the BINS meter (PLAYTEST F3, 18.19): dot order and
 * adult key letters follow the world layout — left/WEST, center/SOUTH,
 * right/EAST. Order is fixed so the row never reshuffles between frames.
 */
const CHUTE_PIP_KEYS: ReadonlyArray<readonly [string, string]> = [
  ["left", "L"], ["center", "C"], ["right", "R"],
];
/**
 * Trash glyph the Hud supplies for the Simple chevron label. The model emits
 * the count ALONE in Simple (early-reader: never the word "BULK", never a
 * compass word); the picture in front of it is presentation, so it lives here.
 */
const CHUTE_SIMPLE_GLYPH = "🗑";
/** Escalation ramp shared by the chevrons and the pips (level 0..3). */
const CHUTE_LEVEL_HEX = [
  cssHex(FEEDBACK_COLORS.muted),
  cssHex(PALETTE.conveyance),
  cssHex(FEEDBACK_COLORS.overflowWarning),
  cssHex(PALETTE.tierHazard),
] as const;
/**
 * Route-freshness presentation (Section 8.3 / 18.10 fourth dogfood): icon +
 * word + signed Grade delta so the state is never hue-only. Colors follow the
 * locked taxonomy: fresh = safe green, repeat = the cool partial blue,
 * stale = the existing STALE amber.
 */
export const FRESHNESS_STYLE: Record<"fresh" | "repeat" | "stale", { icon: string; word: string; color: string }> = {
  fresh: { icon: "▲", word: "FRESH", color: cssHex(PALETTE.tierSafe) },
  repeat: { icon: "▶", word: "REPEAT", color: cssHex(FEEDBACK_COLORS.partial) },
  stale: { icon: "▼", word: "STALE", color: cssHex(FEEDBACK_COLORS.stale) },
};
/** signed Grade delta, minus-sign typography matching the world tags */
export function signedDelta(delta: number): string {
  return delta < 0 ? `−${Math.abs(delta)}` : `+${delta}`;
}
/** Overflow bucket order + facility-voice labels (Section 7 locations, 18.10). */
const BUCKET_ORDER = ["floor", "beam", "transit", "chamber", "belt"] as const;
const BUCKET_LABELS: Record<(typeof BUCKET_ORDER)[number], string> = {
  floor: "FLOOR", beam: "BEAM", transit: "TRANSIT", chamber: "CHAMBER", belt: "BELT",
};

export class Hud {
  private readonly root: HTMLDivElement;

  // central
  private readonly timerEl: HTMLDivElement;
  private readonly scoreEl: HTMLDivElement;
  private readonly scoreValueEl: HTMLSpanElement;

  // grade meter
  private readonly gradeEl: HTMLDivElement;
  private readonly gradeBandEl: HTMLSpanElement;
  private readonly gradeMultEl: HTMLSpanElement;
  private readonly gradeValEl: HTMLSpanElement;
  private readonly gradeFillEl: HTMLDivElement;
  /** transient Grade-delta attribution chip ("+6 VARIED"), latest cause wins */
  private readonly gradeDeltaEl: HTMLSpanElement;
  private gradeDeltaTimer: ReturnType<typeof setTimeout> | null = null;

  // chain ladder
  private readonly chainEl: HTMLDivElement;
  private readonly chainMultEl: HTMLSpanElement;
  private readonly chainPips: HTMLDivElement[] = [];
  private readonly chainTimerFill: HTMLDivElement;

  // dispatch panel
  private readonly dispatchEl: HTMLDivElement;
  private readonly dispatchTimerEl: HTMLSpanElement;
  private readonly recipeRowsEl: HTMLDivElement;
  private readonly dispatchRouteEl: HTMLDivElement;
  private readonly dispatchNextEl: HTMLDivElement;
  private dispatchSignature = "";
  private recipeRows = new Map<string, { fill: HTMLDivElement; count: HTMLSpanElement; row: HTMLDivElement }>();

  // shift status + telegraph + baffle
  private readonly shiftEl: HTMLDivElement;
  private readonly shiftPhaseEl: HTMLDivElement;
  private readonly shiftNextEl: HTMLDivElement;
  private readonly telegraphEl: HTMLDivElement;
  private readonly telegraphHeadEl: HTMLDivElement;
  private readonly telegraphChangesEl: HTMLDivElement;
  private readonly baffleEl: HTMLDivElement;

  // overflow meter
  private readonly overflowEl: HTMLDivElement;
  private readonly overflowIconEl: HTMLSpanElement;
  private readonly overflowTextEl: HTMLSpanElement;
  private readonly overflowCountEl: HTMLSpanElement;
  private readonly overflowFillEl: HTMLDivElement;
  // location breakdown (Section 7 buckets, 18.10)
  private readonly bucketTrackEl: HTMLDivElement;
  private readonly bucketSegEls: Record<keyof HudOverflowBuckets, HTMLDivElement>;
  private readonly overflowWhereEl: HTMLDivElement;
  /** Simple-mode BINS state word ("GETTING FULL!" / "ALMOST FULL!"), 18.11. */
  private readonly binsStateEl: HTMLDivElement;
  private bucketSignature = "";

  // reticle dock
  private readonly dockEl: HTMLDivElement;
  private readonly dockValues: Record<string, HTMLDivElement> = {};
  private dockFuseField!: HTMLDivElement;
  /** ALT-route cell + its separator: hidden unless an alternative beats PROJECTED */
  private dockAltField!: HTMLDivElement;
  private dockAltSep!: HTMLDivElement;
  private routeSignature = "";
  private altSignature = "";

  // conditional/transient
  private readonly recoveryEl: HTMLDivElement;
  private readonly recoveryTextEl: HTMLDivElement;
  private readonly recoveryDetailEl: HTMLDivElement;
  // chamber-heat dwell meter (LOCKED-DESIGN 18.12): a vertical bar beside the reticle
  private readonly chamberHeatEl: HTMLDivElement;
  private readonly chamberHeatFillEl: HTMLDivElement;
  private readonly chamberHeatSecEl: HTMLDivElement;
  private readonly chamberHeatLabelEl: HTMLDivElement;
  // furnace-overload chip on the jam prompt (LOCKED-DESIGN 18.12)
  private readonly overloadEl: HTMLDivElement;
  private readonly alertEl: HTMLDivElement;
  private readonly toastEl: HTMLDivElement;
  private readonly hintEl: HTMLDivElement;
  private readonly fpsEl: HTMLSpanElement;
  private readonly flashEl: HTMLDivElement;
  private readonly vignetteEl: HTMLDivElement;
  /** terminal fault curtain — lazily built by fatalFault(), never hidden again */
  private fatalEl: HTMLDivElement | null = null;

  // Simple-mode kid presentation (LOCKED-DESIGN 18.11): a root class + a couple
  // of transient center overlays. All sizing is token-driven via .tpz-simple.
  private simple = false;
  private tutorialActive = false;
  private countdownEl!: HTMLDivElement;
  private chainWordEl!: HTMLDivElement;
  private chainWordTimer: ReturnType<typeof setTimeout> | null = null;
  // Simple v2 auto-moving portal telegraph (LOCKED-DESIGN 18.11).
  private portalBannerEl!: HTMLDivElement;
  private portalBeaconEl!: HTMLDivElement;
  private portalBeaconLabelEl!: HTMLDivElement;

  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly worldTags = new Map<string, { el: HTMLDivElement; label: HTMLSpanElement; gauge: HTMLDivElement; fill: HTMLDivElement }>();
  private readonly eventFlares = new Map<string, { el: HTMLDivElement; timer: ReturnType<typeof setTimeout> | null }>();
  /**
   * Off-screen chute chevrons (PLAYTEST F3, 18.19). Modelled on `worldTags`
   * (PERSISTENT, retracted by their own cause) rather than on `eventFlares` —
   * the transient 1.8 s timer would fight a cue that must stay up for as long
   * as the backlog does.
   */
  private readonly chuteIndicators = new Map<string, { el: HTMLDivElement; arrow: HTMLSpanElement; label: HTMLSpanElement }>();
  private chutePipRowEl!: HTMLDivElement;
  private readonly chutePips = new Map<string, { dot: HTMLSpanElement; value: HTMLSpanElement }>();
  /**
   * Last `level:bulk` written to each pip. The chevrons genuinely move every
   * frame (they track a projection), but a pip only changes when the backlog
   * does — and this cue is now persistent chrome re-issued 60×/s, so the
   * unchanged case skips its DOM writes entirely. Nothing else writes the pips,
   * so this map is the whole invalidation story.
   */
  private readonly chutePipState = new Map<string, string>();
  private alarmActive = false;
  private displayedScore = 0;
  private lastScore = 0;
  private readonly fpsDebug: boolean;

  /** Gameplay chrome hidden during the tutorial (its numbers aren't live yet). */
  private readonly gameplayEls: HTMLElement[];

  constructor(container: HTMLElement) {
    if (typeof document !== "undefined" && !document.getElementById(HUD_STYLE_ID)) {
      const style = document.createElement("style");
      style.id = HUD_STYLE_ID;
      style.textContent = hudStylesheet();
      document.head.appendChild(style);
    }
    this.fpsDebug =
      typeof location !== "undefined" && new URLSearchParams(location.search).has("fps");

    const root = document.createElement("div");
    root.className = "tpz-hud";
    this.root = root;

    // effect layers first (behind chrome); no data-hud-element — see HudReadability
    this.flashEl = this.make("div", "tpz-flash", root);
    this.vignetteEl = this.make("div", "tpz-vignette", root);

    // central timer + score
    this.timerEl = this.make("div", "tpz-el tpz-timer", root, "timer");
    this.timerEl.textContent = fmtTime(RUN_SECONDS);
    this.scoreEl = this.make("div", "tpz-el tpz-score", root, "score");
    this.scoreValueEl = this.make("span", "tpz-score-value", this.scoreEl);
    this.scoreValueEl.textContent = "0";

    // grade meter
    this.gradeEl = this.make("div", "tpz-el tpz-panel-box tpz-grade", root, "grade");
    const gradeHead = this.make("div", "tpz-meter-head", this.gradeEl);
    const gradeLead = this.make("span", "", gradeHead);
    this.gradeBandEl = this.make("span", "tpz-grade-band", gradeLead);
    this.gradeBandEl.textContent = "C";
    this.gradeMultEl = this.make("span", "tpz-grade-mult", gradeLead);
    this.gradeMultEl.textContent = " ×1.0";
    this.gradeValEl = this.make("span", "tpz-grade-val", gradeHead);
    this.gradeValEl.textContent = "GRADE 25";
    // Grade-delta attribution chip (LOCKED-DESIGN 8.3/18.10): rides the meter
    // head so every Grade movement briefly names its delta at the meter itself.
    this.gradeDeltaEl = this.make("span", "tpz-grade-delta", gradeHead);
    this.gradeDeltaEl.style.display = "none";
    this.gradeFillEl = this.make("div", "tpz-fill", this.make("div", "tpz-track", this.gradeEl));

    // chain ladder
    this.chainEl = this.make("div", "tpz-el tpz-chain", root, "chain");
    const chainHead = this.make("div", "tpz-chain-head", this.chainEl);
    const chainLabel = this.make("span", "tpz-eyebrow", chainHead);
    chainLabel.textContent = "CHAIN";
    this.chainMultEl = this.make("span", "tpz-chain-mult", chainHead);
    this.chainMultEl.textContent = "×1";
    const pips = this.make("div", "tpz-chain-pips", this.chainEl);
    for (let i = 0; i < 5; i++) this.chainPips.push(this.make("div", "tpz-pip", pips));
    this.chainTimerFill = this.make("div", "tpz-fill", this.make("div", "tpz-track tpz-chain-timer", this.chainEl));

    // dispatch panel (top priority; hides when no ticket + no preview)
    this.dispatchEl = this.make("div", "tpz-el tpz-panel-box tpz-dispatch", root, "ticket");
    const dispatchHead = this.make("div", "tpz-dispatch-head", this.dispatchEl);
    const dispatchTitle = this.make("span", "tpz-dispatch-title", dispatchHead);
    dispatchTitle.textContent = "DISPATCH";
    this.dispatchTimerEl = this.make("span", "tpz-dispatch-timer", dispatchHead);
    this.recipeRowsEl = this.make("div", "tpz-recipe-rows", this.dispatchEl);
    this.dispatchRouteEl = this.make("div", "tpz-dispatch-route", this.dispatchEl);
    this.dispatchNextEl = this.make("div", "tpz-dispatch-next", this.dispatchEl);
    this.dispatchEl.style.display = "none";

    // shift status
    this.shiftEl = this.make("div", "tpz-el tpz-shift", root, "phase");
    this.shiftPhaseEl = this.make("div", "tpz-shift-phase", this.shiftEl);
    this.shiftNextEl = this.make("div", "tpz-shift-next", this.shiftEl);

    // shift-change telegraph (warning window only)
    this.telegraphEl = this.make("div", "tpz-el tpz-panel-box tpz-telegraph", root, "shift-telegraph");
    this.telegraphHeadEl = this.make("div", "tpz-telegraph-head", this.telegraphEl);
    this.telegraphChangesEl = this.make("div", "tpz-telegraph-change", this.telegraphEl);
    this.telegraphEl.style.display = "none";

    // hazard baffle gate readout (hazard phases only)
    this.baffleEl = this.make("div", "tpz-el tpz-baffle", root, "baffle-gate");
    this.baffleEl.style.display = "none";

    // overflow edge-pressure meter
    this.overflowEl = this.make("div", "tpz-el tpz-panel-box tpz-overflow", root, "overflow");
    const overflowHead = this.make("div", "tpz-overflow-head", this.overflowEl);
    const overflowLabel = this.make("span", "tpz-overflow-label", overflowHead);
    this.overflowIconEl = this.make("span", "", overflowLabel);
    this.overflowTextEl = this.make("span", "", overflowLabel);
    this.overflowTextEl.textContent = "OVERFLOW";
    this.overflowCountEl = this.make("span", "tpz-overflow-count", overflowHead);
    this.overflowCountEl.textContent = `0/${FACILITY_CAPACITY}`;
    const overflowTrack = this.make("div", "tpz-track tpz-overflow-track", this.overflowEl);
    this.overflowFillEl = this.make("div", "tpz-fill", overflowTrack);
    for (const threshold of [OVERFLOW_WARNING, OVERFLOW_SEVERE]) {
      const tick = this.make("div", "tpz-tick", overflowTrack);
      tick.style.left = `${(threshold / FACILITY_CAPACITY) * 100}%`;
    }
    // segmented location strip + "where" readout (Section 7 buckets, 18.10):
    // the number is never a mystery — chamber/belt segments light when the
    // floor is clean but the bulk is still counted elsewhere.
    this.bucketTrackEl = this.make("div", "tpz-overflow-buckets", this.overflowEl);
    this.bucketSegEls = {
      floor: this.make("div", "tpz-seg", this.bucketTrackEl),
      beam: this.make("div", "tpz-seg", this.bucketTrackEl),
      transit: this.make("div", "tpz-seg", this.bucketTrackEl),
      chamber: this.make("div", "tpz-seg", this.bucketTrackEl),
      belt: this.make("div", "tpz-seg", this.bucketTrackEl),
    };
    for (const key of BUCKET_ORDER) this.bucketSegEls[key].style.background = OVERFLOW_BUCKET_HEX[key];
    this.overflowWhereEl = this.make("div", "tpz-overflow-where", this.overflowEl);
    this.overflowWhereEl.style.display = "none";
    // Simple BINS state word (18.11): one honest escalating line under the bar.
    this.binsStateEl = this.make("div", "tpz-bins-state", this.overflowEl);
    this.binsStateEl.style.display = "none";
    // Per-chute intake pips (PLAYTEST F3 / 18.19): three dots inside the BINS
    // meter, keyed L/C/R, whose fill follows the same escalation as the
    // off-screen chevrons — so the pressure has a resting home even when every
    // chute happens to be on screen. DECORATION inside existing chrome: they
    // carry NO data-hud-element, because an id here would guarantee an overlap
    // failure against `overflow`, their own parent (see HudReadability).
    this.chutePipRowEl = this.make("div", "tpz-chute-pips", this.overflowEl);
    for (const [chute, key] of CHUTE_PIP_KEYS) {
      const pip = this.make("div", "tpz-chute-pip", this.chutePipRowEl);
      const dot = this.make("span", "tpz-chute-pip-dot", pip);
      const keyEl = this.make("span", "tpz-chute-pip-key", pip);
      keyEl.textContent = key;
      // adult-only numeral; Simple shows the dot alone (early-reader, 18.11)
      const value = this.make("span", "tpz-chute-pip-val", pip);
      value.textContent = "0";
      this.chutePips.set(chute, { dot, value });
    }

    // reticle dock (bottom-center, six locked fields + the conditional ALT
    // cell — LOCKED-DESIGN 18.10 fourth dogfood: switching must visibly pay)
    this.dockEl = this.make("div", "tpz-el tpz-panel-box tpz-dock", root, "load-dock");
    const dockFields: Array<[string, string]> = [
      ["bulk", "BULK"], ["comp", "LOAD"], ["fuse", "RED FUSE"],
      ["route", "ROUTE"], ["ticket", "DISPATCH"], ["score", "PROJECTED"], ["alt", "ALT ROUTE"],
    ];
    dockFields.forEach(([key, label], index) => {
      const sep = index > 0 ? this.make("div", "tpz-dock-sep", this.dockEl) : null;
      const field = this.make("div", `tpz-dock-field${key === "fuse" ? " tpz-fuse" : ""}`, this.dockEl);
      const labelEl = this.make("div", "tpz-dock-label", field);
      labelEl.textContent = label;
      this.dockValues[key] = this.make("div", "tpz-dock-value", field);
      if (key === "fuse") this.dockFuseField = field;
      if (key === "alt") {
        this.dockAltField = field;
        this.dockAltSep = sep!;
        field.style.display = "none";
        sep!.style.display = "none";
      }
    });
    this.dockEl.style.display = "none";

    // reticle-dock echo of the jam-recovery prompt (LOCKED-DESIGN 18.10): sits
    // just above the held-load dock, naming the recovery verb; hidden by default.
    this.recoveryEl = this.make("div", "tpz-el tpz-recovery", root, "recovery-prompt");
    // Two lines inside ONE hud element (18.16): the verb chain, plus an optional
    // quieter consequence line (the Section 3.3 interlock). A second
    // data-hud-element would add a fresh overlap partner to every readability
    // sweep for what is one prompt.
    this.recoveryTextEl = this.make("div", "tpz-recovery-text", this.recoveryEl);
    this.recoveryDetailEl = this.make("div", "tpz-recovery-detail", this.recoveryEl);
    this.recoveryDetailEl.style.display = "none";
    this.recoveryEl.style.display = "none";

    // FURNACE OVERLOAD chip (LOCKED-DESIGN 18.12): rides just above the recovery
    // prompt while any lane is jammed, counting the overload clock down. Pins at
    // OVERLOAD HELD past the deadline (feed-grace) so the timer never reads as lying.
    this.overloadEl = this.make("div", "tpz-el tpz-overload", root, "overload-chip");
    this.overloadEl.style.display = "none";

    // CHAMBER HEAT dwell meter (LOCKED-DESIGN 18.12): a vertical heat bar docked
    // beside the reticle, visible only while the player is inside the chamber.
    this.chamberHeatEl = this.make("div", "tpz-el tpz-heat", root, "chamber-heat");
    this.chamberHeatLabelEl = this.make("div", "tpz-heat-label", this.chamberHeatEl);
    this.chamberHeatLabelEl.textContent = "CHAMBER HEAT";
    const heatTrack = this.make("div", "tpz-heat-track", this.chamberHeatEl);
    this.chamberHeatFillEl = this.make("div", "tpz-heat-fill", heatTrack);
    this.chamberHeatSecEl = this.make("div", "tpz-heat-sec", this.chamberHeatEl);
    this.chamberHeatEl.style.display = "none";

    // central alert (critical overflow only — jams are owned by world tags)
    this.alertEl = this.make("div", "tpz-el tpz-alert", root, "alert");

    // transient coach toast
    this.toastEl = this.make("div", "tpz-el tpz-toast", root, "toast");

    // control-hint strip
    this.hintEl = this.make("div", "tpz-el tpz-hint", root, "hint");
    this.hintEl.textContent =
      "WASD move · Space jump · Shift dash · hold LMB/E grab · Q/R portals · C purge · T reset";
    this.fpsEl = this.make("span", "tpz-fps", this.hintEl);
    if (!this.fpsDebug) this.fpsEl.style.display = "none";

    // Simple-mode center announcements (LOCKED-DESIGN 18.11): the placement
    // countdown ("GET READY… 5") and the chain tier-up word ("GOOD!"). These
    // are transient center overlays like the flash/toast; they carry NO
    // data-hud-element (announcements, not persistent chrome, so they never
    // enter the readability snapshot or collide with the required set).
    this.countdownEl = this.make("div", "tpz-el tpz-countdown", root);
    this.countdownEl.style.display = "none";
    this.chainWordEl = this.make("div", "tpz-el tpz-chainword", root);
    this.chainWordEl.style.display = "none";

    // Simple v2 auto-portal telegraph (LOCKED-DESIGN 18.11): a top-center kid
    // banner with a 5→1 count, and a world-anchored destination beacon (pulsing
    // ring + arrow + "OVER HERE!") carrying data-cue-target for QA. Both honor
    // reduced-motion + the Simple 0.2 pulse cap via the .tpz-reduced root class
    // and a <=2Hz keyframe; neither is persistent chrome (no data-hud-element).
    this.portalBannerEl = this.make("div", "tpz-el tpz-portal-banner", root);
    this.portalBannerEl.style.display = "none";
    this.portalBeaconEl = this.make("div", "tpz-el tpz-portal-beacon", root);
    this.portalBeaconEl.dataset.portalCue = "ring";
    this.portalBeaconEl.style.display = "none";
    const beaconArrow = this.make("div", "tpz-portal-beacon-arrow", this.portalBeaconEl);
    beaconArrow.textContent = "▾";
    this.portalBeaconLabelEl = this.make("div", "tpz-portal-beacon-label", this.portalBeaconEl);
    this.portalBeaconLabelEl.textContent = "OVER HERE!";

    this.gameplayEls = [
      this.timerEl, this.scoreEl, this.gradeEl, this.chainEl, this.dispatchEl,
      this.shiftEl, this.telegraphEl, this.baffleEl, this.overflowEl, this.dockEl, this.alertEl,
    ];

    container.appendChild(root);
  }

  private make<K extends keyof HTMLElementTagNameMap>(
    tag: K, className: string, parent?: HTMLElement, hudElement?: string,
  ): HTMLElementTagNameMap[K] {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (hudElement) el.dataset.hudElement = hudElement;
    parent?.appendChild(el);
    return el;
  }

  /**
   * Hide the whole in-arena HUD whenever a shell overlay / non-playing state is
   * active (LOCKED-DESIGN 18.10 residual): the RAF loop is not running before a
   * run, so the idle chrome (timer/score/grade/hint) must not bleed through the
   * title/settings overlays. The shell drives this from its screen state.
   */
  setChromeVisible(visible: boolean): void {
    this.root.style.display = visible ? "" : "none";
    // The chute chevrons are re-issued every frame from the live projection, so
    // a hidden HUD must drop them outright — otherwise a stale chevron from the
    // last run's camera pose would be waiting when the chrome comes back.
    if (!visible) this.setChuteIndicators([]);
  }

  /**
   * Kid-mode density switch (LOCKED-DESIGN 18.11): adds the `tpz-simple` root
   * class (all sizing rides theme tokens in HudStyles) and HIDES the systems the
   * sim still computes but Simple doesn't surface — Grade meter, Chain ladder,
   * dispatch panel, shift status + telegraph, baffle gate, and the reticle dock
   * (the held load is visible in-world). SHOWN: score, timer, BINS meter, hint,
   * pause. Idempotent; safe to call every run.
   */
  setSimpleMode(simple: boolean): void {
    this.simple = simple;
    this.root.classList.toggle("tpz-simple", simple);
    // Persistently hidden chrome (update() never re-shows these):
    const hidden = [this.gradeEl, this.chainEl, this.shiftEl];
    for (const el of hidden) el.style.display = simple ? "none" : "";
    // These already hide themselves when empty in Simple (no tickets / no held
    // dock render / empty shift changes / no hazard), but pin them off so a
    // stray frame can never flash them.
    if (simple) {
      this.dispatchEl.style.display = "none";
      this.telegraphEl.style.display = "none";
      this.baffleEl.style.display = "none";
      this.dockEl.style.display = "none";
    }
    // BINS reframing (18.11): honest single-owner meter, friendlier label.
    this.overflowTextEl.textContent = simple ? "BINS" : "OVERFLOW";
    this.bucketTrackEl.style.display = simple ? "none" : "";
    if (simple) this.overflowWhereEl.style.display = "none";
    this.binsStateEl.style.display = "none";
    // Simplified hint copy (18.11 early-reader voice). The jump is named since
    // 18.25: the intent gate makes it the ONLY way into the chamber, and the jam
    // prompt ("JUMP IN AND FEED THE FIRE!") orders a child to perform it — so a
    // hint line that never mentioned it was asking for a control the game hid.
    this.hintEl.firstChild!.textContent = simple
      ? "WASD move · hold E grab · let go to throw · SPACE jump into the ring"
      : "WASD move · Space jump · Shift dash · hold LMB/E grab · Q/R portals · C purge · T reset";
    if (!simple) {
      this.countdownEl.style.display = "none";
      this.chainWordEl.style.display = "none";
    }
  }

  /**
   * Simple-mode chain tier-up word (LOCKED-DESIGN 18.11): a big star-burst word
   * replacing the hidden Chain ladder. Tiers 1-5 → GOOD!/GREAT!/SUPER!/WOW!/
   * MEGA!. Fires with the existing tier stinger audio (Engine owns the sound).
   */
  chainTierWord(tier: number): void {
    if (!this.simple) return;
    const words = ["GOOD!", "GREAT!", "SUPER!", "WOW!", "MEGA!"];
    const word = words[Math.max(0, Math.min(words.length - 1, tier - 1))]!;
    if (this.chainWordTimer) clearTimeout(this.chainWordTimer);
    this.chainWordEl.textContent = word;
    this.chainWordEl.style.color = CHAIN_TIER_HEX[Math.min(tier, CHAIN_TIER_HEX.length - 1)]!;
    this.chainWordEl.style.display = "block";
    if (!this.root.classList.contains("tpz-reduced")) {
      this.chainWordEl.style.transition = "none";
      this.chainWordEl.style.transform = "translateX(-50%) scale(0.6)";
      requestAnimationFrame(() => {
        this.chainWordEl.style.transition = "transform .28s cubic-bezier(.2,.8,.3,1)";
        this.chainWordEl.style.transform = "translateX(-50%) scale(1)";
      });
    }
    this.chainWordTimer = setTimeout(() => {
      this.chainWordEl.style.display = "none";
      this.chainWordTimer = null;
    }, 1100);
  }

  /**
   * Simple-mode auto-portal telegraph banner (LOCKED-DESIGN 18.11 v2). `text`
   * null hides it; otherwise shows the kid line plus a big 5→1 count. Per-frame
   * idempotent; pulse rides the .tpz-reduced-gated CSS (<=2Hz, no full-screen
   * flash), and the priority audio cue is owned by the Engine (never the alarm
   * voice for a routine move).
   */
  portalBanner(text: string | null, count: string): void {
    if (!this.simple || !text) {
      this.portalBannerEl.style.display = "none";
      return;
    }
    this.portalBannerEl.style.display = "block";
    this.portalBannerEl.innerHTML = "";
    const line = this.make("div", "tpz-portal-banner-line", this.portalBannerEl);
    line.textContent = text;
    if (count) {
      const num = this.make("div", "tpz-portal-banner-count", this.portalBannerEl);
      num.textContent = count;
    }
  }

  /**
   * Simple-mode world-anchored destination beacon (LOCKED-DESIGN 18.11 v2).
   * `spec` null hides it; otherwise position it at the next anchor's projected
   * screen point and stamp data-cue-target=<anchor> for QA. Clamped into the HUD
   * safe area like world tags so it never chases a point off-frame.
   */
  portalBeacon(spec: { x: number; y: number; target: string } | null): void {
    if (!this.simple || !spec) {
      this.portalBeaconEl.style.display = "none";
      this.portalBeaconEl.dataset.cueTarget = "";
      return;
    }
    this.portalBeaconEl.style.display = "block";
    this.portalBeaconEl.dataset.cueTarget = spec.target;
    const rootRect = this.root.getBoundingClientRect();
    const beaconRect = this.portalBeaconEl.getBoundingClientRect();
    const halfW = beaconRect.width / 2;
    const halfH = beaconRect.height / 2;
    const margin = HUD_SAFE_MARGIN_PX + 1;
    const cx = rootRect.width > 0 ? Math.min(Math.max(spec.x, margin + halfW), rootRect.width - margin - halfW) : spec.x;
    const cy = rootRect.height > 0 ? Math.min(Math.max(spec.y, margin + halfH), rootRect.height - margin - halfH) : spec.y;
    this.portalBeaconEl.style.left = `${cx.toFixed(1)}px`;
    this.portalBeaconEl.style.top = `${cy.toFixed(1)}px`;
  }

  /**
   * Reticle-dock echo of the jam-recovery prompt (LOCKED-DESIGN 18.10). `text`
   * names the recovery verb + what it does; null hides it. `urgent` escalates
   * the styling when the player keeps feeding a jammed lane. `detail` is the
   * optional quieter second line naming the rule that removed the player's
   * other option (18.16: the Section 3.3 portal interlock) — adult only.
   */
  setRecoveryPrompt(text: string | null, urgent: boolean, detail: string | null = null): void {
    if (!text) {
      this.recoveryEl.style.display = "none";
      return;
    }
    this.recoveryEl.style.display = "block";
    this.recoveryTextEl.textContent = text;
    this.recoveryDetailEl.textContent = detail ?? "";
    this.recoveryDetailEl.style.display = detail ? "block" : "none";
    this.recoveryEl.classList.toggle("tpz-urgent", urgent);
  }

  /**
   * CHAMBER HEAT dwell meter (LOCKED-DESIGN 18.12 stage 3): visible only while the
   * player is inside the chamber. `frac` = elapsed dwell fraction (fill), `seconds`
   * = remaining. At the 2s warning the bar flips red and reads GET OUT — COOKING
   * (steady red under flash reduction, never a strobe; the camera never shakes).
   * null hides it (exit resets instantly). Kid copy swaps the labels in Simple.
   */
  setChamberHeat(state: { frac: number; seconds: number; warning: boolean; simple: boolean; reduced: boolean } | null): void {
    if (!state) {
      this.chamberHeatEl.style.display = "none";
      return;
    }
    this.chamberHeatEl.style.display = "block";
    const pct = Math.max(0, Math.min(1, state.frac)) * 100;
    this.chamberHeatFillEl.style.height = `${pct.toFixed(1)}%`;
    this.chamberHeatLabelEl.textContent = state.warning
      ? (state.simple ? "TOO HOT! RUN OUT!" : "GET OUT — COOKING")
      : (state.simple ? "HOT!" : "CHAMBER HEAT");
    this.chamberHeatSecEl.textContent = `${Math.max(0, state.seconds).toFixed(1)}s`;
    // warning = red bar; flash reduction / reduced-motion keeps it STEADY red.
    this.chamberHeatEl.classList.toggle("tpz-warning", state.warning);
    this.chamberHeatEl.classList.toggle("tpz-steady", state.warning && state.reduced);
  }

  /**
   * FURNACE OVERLOAD chip (LOCKED-DESIGN 18.12): appears the instant any lane jams.
   * `held` = past the deadline but inside feed-grace — the chip pins at OVERLOAD
   * HELD (never refills) so the countdown never reads as lying. null hides it.
   */
  setOverload(state: { seconds: number; held: boolean; simple: boolean } | null): void {
    if (!state) {
      this.overloadEl.style.display = "none";
      return;
    }
    this.overloadEl.style.display = "block";
    this.overloadEl.textContent = state.held
      ? "OVERLOAD HELD — KEEP FEEDING"
      : (state.simple ? "THE FURNACE IS TOO FULL — FEED IT!" : `FURNACE OVERLOAD · ${state.seconds}s`);
    this.overloadEl.classList.toggle("tpz-held", state.held);
  }

  /** Hide gameplay chrome during the tutorial — those numbers aren't live yet. */
  setTutorialMode(active: boolean): void {
    this.tutorialActive = active;
    const opacity = active ? "0" : "1";
    for (const el of this.gameplayEls) {
      el.style.transition = "opacity .3s";
      el.style.opacity = opacity;
    }
    // The Simple placement countdown ("GET READY… 5") is a separate center
    // overlay outside gameplayEls, and during the tutorial the sim never steps
    // so placementSeconds is pinned at 5 (LOCKED-DESIGN 18.11: it would sit
    // frozen mid-screen contradicting the "MOVE!" orientation card). Hide it
    // outright while the tutorial owns the screen; the render gate below keeps
    // it hidden until the real shift ready-beat begins.
    if (active) {
      this.countdownEl.style.display = "none";
      this.countdownEl.classList.remove("tpz-go");
    }
  }

  /** Floating "+300" at a container-space position. */
  popup(x: number, y: number, text: string, color = cssHex(FEEDBACK_COLORS.stale)): void {
    const el = this.make("div", "tpz-popup", this.root);
    el.textContent = text;
    el.style.left = `${Math.round(x)}px`;
    el.style.top = `${Math.round(y)}px`;
    el.style.color = color;
    el.style.fontSize = "20px";
    el.style.fontWeight = "700";
    requestAnimationFrame(() => {
      el.style.transform = "translate(-50%,-160%) scale(1.15)";
      el.style.opacity = "0";
    });
    setTimeout(() => el.remove(), 750);
  }

  /**
   * Grade-delta attribution chip (LOCKED-DESIGN 8.3 / 18.10 fourth dogfood):
   * briefly shows the signed delta + cause word at the Grade meter head.
   * A single element — the latest cause wins, so a busy volley coalesces
   * instead of stacking chips.
   */
  gradeDelta(text: string, color: string): void {
    if (this.gradeDeltaTimer) clearTimeout(this.gradeDeltaTimer);
    this.gradeDeltaEl.textContent = text;
    this.gradeDeltaEl.style.color = color;
    this.gradeDeltaEl.style.display = "";
    this.gradeDeltaTimer = setTimeout(() => {
      this.gradeDeltaEl.style.display = "none";
      this.gradeDeltaTimer = null;
    }, 1400);
  }

  /** Pulse the chain readout when the chain grows. */
  pulseCombo(): void {
    this.chainMultEl.style.transition = "none";
    this.chainMultEl.style.transform = "scale(1.45)";
    requestAnimationFrame(() => {
      this.chainMultEl.style.transition = "transform .25s ease-out";
      this.chainMultEl.style.transform = "scale(1)";
    });
  }

  /** Chain reset: flash the chain readout in the deny color, then settle. */
  chainBreakFlash(): void {
    this.chainMultEl.style.transition = "none";
    this.chainMultEl.style.color = cssHex(FEEDBACK_COLORS.deny);
    this.chainMultEl.style.transform = "scale(1.2)";
    requestAnimationFrame(() => {
      this.chainMultEl.style.transition = "transform .3s ease-out, color .6s ease-out";
      this.chainMultEl.style.transform = "scale(1)";
      this.chainMultEl.style.color = "";
    });
  }

  /**
   * Full-screen flash. Callers pass ALREADY knob-adjusted peak/duration —
   * Engine.flash() owns flash-reduction and prefers-reduced-motion policy.
   */
  flash(color: string, peak: number, durationSeconds: number): void {
    this.flashEl.style.transition = "none";
    this.flashEl.style.background = color;
    this.flashEl.style.opacity = String(peak);
    requestAnimationFrame(() => {
      this.flashEl.style.transition = `opacity ${durationSeconds}s ease-out`;
      this.flashEl.style.opacity = "0";
    });
  }

  /**
   * Critical-overflow alarm treatment (LOCKED-DESIGN 18.4). `pulse` 0..1 drives
   * the edge-vignette intensity per frame; flash-reduced callers pass a steady
   * value instead of a pulse.
   */
  setAlarm(active: boolean, pulse = 1): void {
    this.alarmActive = active;
    if (!active) {
      this.vignetteEl.style.display = "none";
      return;
    }
    this.vignetteEl.style.display = "block";
    const alarm = cssHex(FEEDBACK_COLORS.alarm);
    const spread = Math.round(30 + pulse * 60);
    this.vignetteEl.style.boxShadow = `inset 0 0 ${spread}px ${Math.round(spread / 3)}px ${alarm}`;
    this.vignetteEl.style.opacity = String(0.35 + pulse * 0.45);
  }

  /** Pre-critical overflow pressure (24/30 thresholds): a static quieter edge tint. */
  setPressure(level: "warning" | "severe" | null): void {
    if (this.alarmActive) return;
    if (level === null) {
      this.vignetteEl.style.display = "none";
      return;
    }
    const color = cssHex(level === "severe" ? FEEDBACK_COLORS.alarm : FEEDBACK_COLORS.overflowWarning);
    this.vignetteEl.style.display = "block";
    this.vignetteEl.style.boxShadow = `inset 0 0 ${level === "severe" ? 46 : 26}px 8px ${color}`;
    this.vignetteEl.style.opacity = level === "severe" ? "0.4" : "0.22";
  }

  /**
   * Terminal fault curtain (physics-fault containment): shown once when the
   * engine halts a run it cannot continue — a wasm physics panic poisons the
   * world permanently, so the honest surface is a loud full-stop with a
   * reload instruction, never a frozen unexplained arena. Effect-layer, not
   * chrome: carries no data-hud-element (HudReadability treats inset:0
   * layers as non-chrome) and never hides again.
   */
  fatalFault(title: string, body: string): void {
    if (!this.fatalEl) {
      this.fatalEl = this.make("div", "tpz-fatal", this.root);
      const card = this.make("div", "tpz-fatal-card", this.fatalEl);
      this.make("div", "tpz-fatal-title", card).textContent = title;
      this.make("div", "tpz-fatal-body", card).textContent = body;
    }
    this.fatalEl.style.display = "flex";
  }

  /** Transient center toast (coach/facility voice). */
  toast(text: string, color = "#dfe7f2", durationMs = 2600): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastEl.textContent = text;
    this.toastEl.style.color = color;
    this.toastEl.style.display = "block";
    this.toastEl.style.opacity = "1";
    this.toastTimer = setTimeout(() => {
      this.toastEl.style.display = "none";
      this.toastTimer = null;
    }, durationMs);
  }

  /**
   * World-anchored micro-label (jam countdowns, STALE route). Update per frame.
   * `frac` (0..1) additionally shows a linear progress gauge under the text —
   * the lever-pull act-confirmation cue (LOCKED-DESIGN 18.10).
   */
  worldTag(id: string, x: number, y: number, text: string, color: string, frac: number | null = null): void {
    let entry = this.worldTags.get(id);
    if (!entry) {
      const el = this.make("div", "tpz-el tpz-world", this.root, `world-${id}`);
      const label = this.make("span", "", el);
      const gauge = this.make("div", "tpz-world-gauge", el);
      const fill = this.make("div", "tpz-world-gauge-fill", gauge);
      entry = { el, label, gauge, fill };
      this.worldTags.set(id, entry);
    }
    const el = entry.el;
    el.style.color = color;
    entry.label.textContent = text;
    entry.gauge.style.display = frac === null ? "none" : "";
    if (frac !== null) entry.fill.style.width = `${clamp(frac * 100, 0, 100)}%`;
    // Clamp into the HUD safe area: projected world anchors can leave the frame
    // (camera blends, behind-camera points) and chrome must never follow them
    // off-screen (HudReadability safe-area rule).
    const rootRect = this.root.getBoundingClientRect();
    const tagRect = el.getBoundingClientRect();
    const halfW = tagRect.width / 2;
    const halfH = tagRect.height / 2;
    const margin = HUD_SAFE_MARGIN_PX + 1;
    const cx = rootRect.width > 0 ? Math.min(Math.max(x, margin + halfW), rootRect.width - margin - halfW) : x;
    const cy = rootRect.height > 0 ? Math.min(Math.max(y, margin + halfH), rootRect.height - margin - halfH) : y;
    el.style.left = `${cx.toFixed(1)}px`;
    el.style.top = `${cy.toFixed(1)}px`;
  }

  clearWorldTag(id: string): void {
    const entry = this.worldTags.get(id);
    if (!entry) return;
    entry.el.remove();
    this.worldTags.delete(id);
  }

  /**
   * Off-screen event indicator (LOCKED-DESIGN 18.10): when a scoring-relevant
   * event fires off-camera, drop a directional edge cue pointing toward it so a
   * jam forming, a red-orb detonation, or the critical shutters is never a
   * surprise. On-screen events are already covered by popups/world tags, so a
   * point inside the visible frame is a no-op. Transient: auto-clears.
   */
  edgeFlare(id: string, x: number, y: number, text: string, color: string): void {
    const rootRect = this.root.getBoundingClientRect();
    const w = rootRect.width;
    const h = rootRect.height;
    if (w <= 0 || h <= 0) return;
    // Shared centre-band maths (ChuteIndicators.edgeBandPlacement): the chute
    // indicator channel places its chevrons with the SAME margin, edge scale,
    // band clamp and octant, so the two off-screen channels cannot drift apart.
    const placement = edgeBandPlacement({ x, y }, { width: w, height: h });
    if (placement.inside) {
      this.clearEdgeFlare(id);
      return;
    }
    const { x: px, y: py, octant } = placement;

    let entry = this.eventFlares.get(id);
    if (entry) clearTimeout(entry.timer ?? undefined);
    if (!entry) {
      const el = this.make("div", "tpz-el tpz-event", this.root, `event-${id}`);
      const arrow = this.make("span", "tpz-event-arrow", el);
      const label = this.make("span", "", el);
      arrow.dataset.role = "arrow";
      label.dataset.role = "label";
      // No timer here: the single assignment below owns it. Arming one on the
      // creation path too left an unrecoverable orphan that tore a PERSISTENT
      // re-issued flare (the jam entrance chevron) down every 1.8 s.
      entry = { el, timer: null };
      this.eventFlares.set(id, entry);
    }
    entry.timer = setTimeout(() => this.clearEdgeFlare(id), 1800);
    const el = entry.el;
    el.style.left = `${px.toFixed(1)}px`;
    el.style.top = `${py.toFixed(1)}px`;
    el.style.color = color;
    el.style.borderColor = color;
    el.style.opacity = "1";
    (el.querySelector('[data-role="arrow"]') as HTMLElement).textContent = ARROW_GLYPHS[octant]!;
    (el.querySelector('[data-role="label"]') as HTMLElement).textContent = text;
  }

  /**
   * Public because a PERSISTENT flare (18.16's jam entrance chevron, re-issued
   * every frame while the jam lasts) must be retractable the instant its cause
   * clears, rather than lingering for the 1.8 s transient timeout.
   */
  clearEdgeFlare(id: string): void {
    const entry = this.eventFlares.get(id);
    if (!entry) return;
    clearTimeout(entry.timer ?? undefined);
    entry.el.remove();
    this.eventFlares.delete(id);
  }

  /**
   * Band points currently owned by the EVENT channel, for the chute model's
   * cross-channel anti-collision (18.19). Both channels clamp into the same
   * `edgeBandPlacement` band and both are `data-hud-element`s, so a persistent
   * adult jam flare and a backlogged off-screen chute in the same octant would
   * otherwise stack two pills on nearly the same point. Sorted by id so the
   * chevron rows stay deterministic.
   */
  eventFlareAnchors(): ReservedBandPoint[] {
    const anchors: ReservedBandPoint[] = [];
    for (const [id, entry] of this.eventFlares) {
      anchors.push({ id, x: Number.parseFloat(entry.el.style.left), y: Number.parseFloat(entry.el.style.top) });
    }
    return anchors
      .filter((anchor) => Number.isFinite(anchor.x) && Number.isFinite(anchor.y))
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  }

  /**
   * Off-screen chute accumulation channel (PLAYTEST F3 part 2, 18.19). Binds
   * the pure `chuteIndicatorModel` output onto two surfaces:
   *
   *  - three PERSISTENT directional chevrons (`data-hud-element="chute-left|
   *    chute-center|chute-right"`), so they obey the same containment/overlap
   *    rules as every other piece of chrome. The model's anti-collision pass is
   *    what keeps them legal when two chutes fall off the same frame edge.
   *  - the per-chute pips inside the BINS/OVERFLOW meter, which follow `level`
   *    in BOTH modes whether or not a chevron is up.
   *
   * COMPOSITION: a chute that is on screen is reported `hidden` by the model —
   * its in-world mini-batch mouth flash (`arena.setChuteTelegraph`) already
   * owns that cue. The chevron is strictly the off-screen channel; the two are
   * never up for the same chute at the same time.
   *
   * Passing `[]` retracts everything (tutorial, run end, chrome hidden).
   */
  setChuteIndicators(specs: readonly ChuteIndicatorSpec[]): void {
    const seen = new Set<string>();
    for (const spec of specs) {
      seen.add(spec.id);
      const pip = this.chutePips.get(spec.id);
      const pipState = `${spec.level}:${spec.bulk}`;
      if (pip && this.chutePipState.get(spec.id) !== pipState) {
        this.chutePipState.set(spec.id, pipState);
        pip.dot.style.background = CHUTE_LEVEL_HEX[spec.level];
        pip.dot.style.opacity = spec.level === 0 ? "0.32" : "1";
        pip.value.textContent = String(spec.bulk);
      }
      if (spec.kind !== "edge") {
        this.clearChuteIndicator(spec.id);
        continue;
      }
      let entry = this.chuteIndicators.get(spec.id);
      if (!entry) {
        const el = this.make("div", "tpz-el tpz-event tpz-chute", this.root, `chute-${spec.id}`);
        const arrow = this.make("span", "tpz-event-arrow", el);
        const label = this.make("span", "", el);
        entry = { el, arrow, label };
        this.chuteIndicators.set(spec.id, entry);
      }
      const color = CHUTE_LEVEL_HEX[spec.level];
      entry.el.style.left = `${spec.x.toFixed(1)}px`;
      entry.el.style.top = `${spec.y.toFixed(1)}px`;
      entry.el.style.color = color;
      entry.el.style.borderColor = color;
      entry.el.classList.toggle("tpz-chute-full", spec.pulse);
      entry.arrow.textContent = ARROW_GLYPHS[spec.octant] ?? "→";
      entry.label.textContent = this.simple ? `${CHUTE_SIMPLE_GLYPH} ${spec.label}` : spec.label;
    }
    for (const id of [...this.chuteIndicators.keys()]) if (!seen.has(id)) this.clearChuteIndicator(id);
    if (specs.length === 0) {
      // Full retraction: reset the pips AND the memo, so the first spec after a
      // retraction always writes even if it repeats the pre-retraction value.
      this.chutePipState.clear();
      for (const [, pip] of this.chutePips) {
        pip.dot.style.background = CHUTE_LEVEL_HEX[0];
        pip.dot.style.opacity = "0.32";
        pip.value.textContent = "0";
      }
    }
  }

  private clearChuteIndicator(id: string): void {
    const entry = this.chuteIndicators.get(id);
    if (!entry) return;
    entry.el.remove();
    this.chuteIndicators.delete(id);
  }

  update(model: HudModel): void {
    this.root.classList.toggle("tpz-reduced", model.motionReduced);

    // score: count/tick animation toward the live value (snaps when reduced)
    if (model.score !== this.lastScore) {
      this.lastScore = model.score;
      if (!model.motionReduced) this.pulseScore();
    }
    if (model.motionReduced || Math.abs(model.score - this.displayedScore) < 1) {
      this.displayedScore = model.score;
    } else {
      this.displayedScore += (model.score - this.displayedScore) * 0.28;
      if (Math.abs(model.score - this.displayedScore) < 1) this.displayedScore = model.score;
    }
    this.scoreValueEl.textContent = Math.round(this.displayedScore).toLocaleString();

    // timer
    this.timerEl.textContent = fmtTime(model.timeLeft);
    this.timerEl.classList.toggle("tpz-urgent", model.timeLeft <= 10);

    this.renderGrade(model.grade);
    this.renderChain(model);
    this.renderDispatch(model.activeTicket, model.nextTicket);
    this.renderShift(model.shift);
    this.renderBaffle(model.baffleGate);
    this.renderOverflow(model.overflow, model.overflowBuckets, model.criticalSeconds);
    this.renderReticle(model.reticle, model.activeTicket);

    this.alertEl.style.display = model.criticalSeconds !== null ? "block" : "none";
    if (model.criticalSeconds !== null) {
      // Simple reframes the critical alert warmly (LOCKED-DESIGN 18.11) — same
      // 8s countdown, same alarm vignette, kid words.
      this.alertEl.textContent = this.simple
        ? `TOO FULL! ${model.criticalSeconds.toFixed(1)}`
        : `▲ CRITICAL OVERFLOW · ${model.criticalSeconds.toFixed(1)}s`;
    }

    // Simple placement countdown (18.11): the shift panel is hidden, so the
    // locked 5s ready-beat renders as a big center "GET READY… n" → "GO!".
    // Suppressed during the kid orientation — the sim doesn't step while the
    // tutorial is active, so placementSeconds stays pinned at 5 and the banner
    // would sit frozen over the arena, contradicting the "MOVE!" card.
    if (this.simple && !this.tutorialActive) this.renderCountdown(model.shift.placementSeconds);
  }

  /** Simple-mode placement countdown overlay (LOCKED-DESIGN 18.11). */
  private renderCountdown(placementSeconds: number): void {
    if (placementSeconds > 0) {
      this.countdownEl.style.display = "block";
      this.countdownEl.textContent = `GET READY… ${Math.ceil(placementSeconds)}`;
      this.countdownEl.classList.remove("tpz-go");
    } else if (this.countdownEl.style.display === "block" && !this.countdownEl.classList.contains("tpz-go")) {
      // Hit zero this frame: flash GO! once, then clear.
      this.countdownEl.textContent = "GO!";
      this.countdownEl.classList.add("tpz-go");
      setTimeout(() => {
        this.countdownEl.style.display = "none";
        this.countdownEl.classList.remove("tpz-go");
      }, 700);
    }
  }

  private pulseScore(): void {
    this.scoreEl.classList.remove("tpz-anim");
    this.scoreValueEl.style.transform = "scale(1.16)";
    requestAnimationFrame(() => {
      this.scoreEl.classList.add("tpz-anim");
      this.scoreValueEl.style.transform = "scale(1)";
    });
  }

  private renderGrade(grade: number): void {
    const band = grade >= 85 ? "S" : grade >= 65 ? "A" : grade >= 40 ? "B" : "C";
    const mult = band === "S" ? "1.5" : band === "A" ? "1.25" : band === "B" ? "1.1" : "1.0";
    const color = GRADE_BAND_HEX[band];
    this.gradeBandEl.textContent = band;
    this.gradeBandEl.style.color = color;
    this.gradeMultEl.textContent = ` ×${mult}`;
    this.gradeValEl.textContent = `GRADE ${Math.round(grade)}`;
    this.gradeFillEl.style.width = `${clamp(grade, 0, 100)}%`;
    this.gradeFillEl.style.background = color;
  }

  private renderChain(model: HudModel): void {
    this.chainMultEl.textContent = `×${model.chainMultiplier}`;
    for (let i = 0; i < this.chainPips.length; i++) {
      const lit = i <= model.chainTier;
      const pip = this.chainPips[i]!;
      pip.style.background = lit ? CHAIN_TIER_HEX[i]! : "";
      pip.style.boxShadow = lit ? `0 0 6px ${CHAIN_TIER_HEX[i]}` : "";
    }
    this.chainTimerFill.style.width = `${clamp(model.chainFrac * 100, 0, 100)}%`;
    this.chainTimerFill.style.background = CHAIN_TIER_HEX[Math.min(model.chainTier, CHAIN_TIER_HEX.length - 1)]!;
  }

  private renderDispatch(active: HudActiveTicket | null, next: HudTicketPreview | null): void {
    if (!active && !next) {
      this.dispatchEl.style.display = "none";
      return;
    }
    this.dispatchEl.style.display = "";
    const signature = `${active ? active.entries.map((e) => e.kind).join(",") + active.receiver : "none"}`;
    if (signature !== this.dispatchSignature) {
      this.dispatchSignature = signature;
      this.recipeRowsEl.replaceChildren();
      this.recipeRows.clear();
      for (const entry of active?.entries ?? []) {
        const row = this.make("div", "tpz-recipe-row", this.recipeRowsEl);
        const label = this.make("span", "tpz-recipe-label", row);
        label.textContent = entry.kind.toUpperCase();
        const fill = this.make("div", "tpz-fill", this.make("div", "tpz-track tpz-recipe-track", row));
        fill.style.background = cssHex(FEEDBACK_COLORS.overflowWarning);
        const count = this.make("span", "tpz-recipe-count", row);
        this.recipeRows.set(entry.kind, { fill, count, row });
      }
    }
    if (active) {
      this.dispatchTimerEl.textContent = `${Math.ceil(active.secondsLeft)}s`;
      for (const entry of active.entries) {
        const row = this.recipeRows.get(entry.kind);
        if (!row) continue;
        const done = entry.have >= entry.required;
        row.fill.style.width = `${entry.required > 0 ? clamp((entry.have / entry.required) * 100, 0, 100) : 0}%`;
        row.count.textContent = `${entry.have}/${entry.required}`;
        row.row.classList.toggle("tpz-done", done);
      }
      this.dispatchRouteEl.style.display = active.receiver ? "" : "none";
      if (active.receiver) {
        this.dispatchRouteEl.innerHTML = `ROUTE → <span class="tpz-chip">${active.receiver.toUpperCase()}</span>`;
        (this.dispatchRouteEl.querySelector(".tpz-chip") as HTMLElement).style.color = RECEIVER_TIER_HEX[active.receiver as keyof typeof RECEIVER_TIER_HEX] ?? "";
      }
    } else {
      this.dispatchTimerEl.textContent = "";
      this.dispatchRouteEl.style.display = "none";
    }
    if (next) {
      this.dispatchNextEl.style.display = "";
      const recipe = next.entries.map((e) => `${e.required} ${e.kind}`).join(" + ");
      const route = next.receiver ? ` → ${next.receiver.toUpperCase()}` : "";
      this.dispatchNextEl.innerHTML = `NEXT · <b>${recipe}${route}</b> · in ${Math.ceil(next.startsIn)}s`;
    } else {
      this.dispatchNextEl.style.display = "none";
    }
  }

  private renderShift(shift: HudShift): void {
    this.shiftPhaseEl.textContent = `PHASE ${shift.phaseIndex}/6 · ${shift.label.toUpperCase()}`;
    if (shift.placementSeconds > 0) {
      this.shiftNextEl.textContent = `SHIFT STARTS · ${Math.ceil(shift.placementSeconds)}s`;
    } else {
      this.shiftNextEl.textContent = `NEXT · ${shift.nextLabel.toUpperCase()} · ${Math.ceil(shift.secondsToNext)}s`;
    }

    if (shift.warning && shift.changes.length > 0) {
      this.telegraphEl.style.display = "";
      const imminent = shift.secondsToNext <= 3;
      this.telegraphEl.classList.toggle("tpz-imminent", imminent);
      this.telegraphHeadEl.textContent = `⚠ NEXT SHIFT · ${Math.ceil(shift.secondsToNext)}s`;
      this.telegraphChangesEl.innerHTML = shift.changes.map((c) => c.toUpperCase()).join("<br>");
    } else {
      this.telegraphEl.style.display = "none";
    }
  }

  private renderBaffle(gate: HudBaffleGate | null): void {
    if (!gate) {
      this.baffleEl.style.display = "none";
      return;
    }
    this.baffleEl.style.display = "";
    this.baffleEl.textContent = gate.blocked
      ? `HAZARD GATE · OPENS ${gate.secondsToOpen.toFixed(1)}s`
      : `HAZARD GATE · OPEN ${gate.secondsToClose.toFixed(1)}s`;
    this.baffleEl.style.color = gate.blocked ? cssHex(FEEDBACK_COLORS.alarm) : cssHex(0x3bc9a8);
  }

  private renderOverflow(overflow: number, buckets: HudOverflowBuckets, criticalSeconds: number | null): void {
    const severe = overflow >= OVERFLOW_SEVERE || criticalSeconds !== null;
    const warn = overflow >= OVERFLOW_WARNING;
    this.overflowEl.classList.toggle("tpz-severe", severe);
    this.overflowEl.classList.toggle("tpz-warn", warn && !severe);
    this.overflowIconEl.textContent = warn ? "▲ " : "";
    this.overflowCountEl.textContent = `${Math.round(overflow)}/${FACILITY_CAPACITY}`;
    this.overflowFillEl.style.width = `${clamp((overflow / FACILITY_CAPACITY) * 100, 0, 100)}%`;

    // Simple BINS meter (LOCKED-DESIGN 18.11): one honest fill, no location
    // strip, and a friendly escalating state word at the same locked thresholds.
    if (this.simple) {
      const value = Math.round(overflow);
      const state = criticalSeconds !== null
        ? "TOO FULL!"
        : value >= OVERFLOW_SEVERE
          ? "ALMOST FULL!"
          : value >= OVERFLOW_WARNING
            ? "GETTING FULL!"
            : "";
      this.binsStateEl.textContent = state;
      this.binsStateEl.style.display = state ? "" : "none";
      this.binsStateEl.classList.toggle("tpz-severe", severe);
      return;
    }

    // location breakdown (Section 7 / 18.10): segment widths share the same
    // capacity scale as the pressure bar; the readout lists only non-empty
    // buckets so the common case stays one short line, never a spreadsheet.
    const signature = BUCKET_ORDER.map((key) => buckets[key]).join(",");
    if (signature === this.bucketSignature) return;
    this.bucketSignature = signature;
    let cursor = 0;
    for (const key of BUCKET_ORDER) {
      const seg = this.bucketSegEls[key];
      const width = clamp((buckets[key] / FACILITY_CAPACITY) * 100, 0, 100);
      seg.style.left = `${clamp(cursor, 0, 100)}%`;
      seg.style.width = `${width}%`;
      cursor += width;
    }
    const parts = BUCKET_ORDER.filter((key) => buckets[key] > 0);
    if (parts.length === 0) {
      this.overflowWhereEl.style.display = "none";
      return;
    }
    this.overflowWhereEl.style.display = "";
    this.overflowWhereEl.replaceChildren(
      ...parts.flatMap((key, index) => {
        const span = document.createElement("span");
        span.style.color = OVERFLOW_BUCKET_HEX[key];
        span.textContent = `${BUCKET_LABELS[key]} ${buckets[key]}`;
        return index > 0 ? [document.createTextNode(" · "), span] : [span];
      }),
    );
  }

  private renderReticle(reticle: HudReticle | null, active: HudActiveTicket | null): void {
    // Simple hides the reticle dock entirely (LOCKED-DESIGN 18.11): the held
    // load is visible in-world; there is no route/ALT/projected arithmetic to
    // read. The sim still computes everything — this only stops surfacing it.
    if (this.simple) {
      this.dockEl.style.display = "none";
      return;
    }
    if (!reticle) {
      this.dockEl.style.display = "none";
      return;
    }
    this.dockEl.style.display = "";
    this.dockValues.bulk!.textContent = `${reticle.bulk}/${reticle.capacity}`;
    this.dockValues.comp!.textContent = reticle.composition.length
      ? reticle.composition.map((c) => `${c.count}${KIND_SHORT[c.kind] ?? c.kind.toUpperCase()}`).join(" ")
      : "—";
    this.dockValues.fuse!.textContent = reticle.redFuse === null ? "—" : `${reticle.redFuse.toFixed(1)}s`;
    this.dockFuseField.style.opacity = reticle.redFuse === null ? "0.5" : "1";
    // ROUTE + Section 8.3 freshness state (LOCKED-DESIGN 18.10 fourth dogfood):
    // icon + word + Grade delta, color-coded but never hue-only.
    const style = FRESHNESS_STYLE[reticle.freshness.state];
    const routeSignature = `${reticle.route.anchor}/${reticle.route.receiver}/${reticle.freshness.state}`;
    if (routeSignature !== this.routeSignature) {
      this.routeSignature = routeSignature;
      const routeEl = this.dockValues.route!;
      routeEl.textContent = `${reticle.route.anchor.toUpperCase()}→${reticle.route.receiver.toUpperCase()} `;
      const state = this.make("span", "tpz-route-state", routeEl);
      state.textContent = `· ${style.icon} ${style.word} ${signedDelta(reticle.freshness.delta)}`;
      state.style.color = style.color;
    }
    this.dockValues.ticket!.textContent = active && reticle.ticketContribution !== null
      ? (reticle.ticketContribution > 0 ? `+${reticle.ticketContribution}` : "0")
      : "—";
    this.dockValues.score!.textContent = reticle.projectedScore > 0 ? `+${reticle.projectedScore.toLocaleString()}` : "—";
    // ALT cell: the same load on the best alternative route, shown only when
    // switching visibly pays (18.10). One glanceable cell, never a table. It
    // names the endpoint that CHANGES (a same-receiver anchor swap must not
    // read as "switch to the receiver you're already on") and carries the
    // Section 8.3 Grade consequence that justifies the switch.
    const alt = reticle.projectedAlt;
    this.dockAltField.style.display = alt ? "" : "none";
    this.dockAltSep.style.display = alt ? "" : "none";
    if (!alt) {
      this.altSignature = "";
      return;
    }
    const label = alt.receiver !== reticle.route.receiver
      ? RECEIVER_SHORT[alt.receiver] ?? alt.receiver.toUpperCase()
      : alt.anchor.toUpperCase();
    const altStyle = FRESHNESS_STYLE[alt.freshness.state];
    const altSignature = `${label}/${alt.receiver}/${alt.score}/${alt.freshness.state}`;
    if (altSignature !== this.altSignature) {
      this.altSignature = altSignature;
      const altEl = this.dockValues.alt!;
      altEl.textContent = `↑ ${label} +${alt.score.toLocaleString()} `;
      altEl.style.color = RECEIVER_TIER_HEX[alt.receiver as keyof typeof RECEIVER_TIER_HEX] ?? "";
      const state = this.make("span", "tpz-route-state", altEl);
      state.textContent = `· ${altStyle.icon} ${altStyle.word} ${signedDelta(alt.freshness.delta)}`;
      state.style.color = altStyle.color;
    }
  }

  setFps(fps: number): void {
    if (this.fpsDebug) this.fpsEl.textContent = `${fps} fps`;
  }

  destroy(): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    if (this.gradeDeltaTimer) clearTimeout(this.gradeDeltaTimer);
    if (this.chainWordTimer) clearTimeout(this.chainWordTimer);
    for (const [, entry] of this.eventFlares) clearTimeout(entry.timer ?? undefined);
    this.eventFlares.clear();
    this.setChuteIndicators([]);
    this.root.remove();
  }
}

function fmtTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
